import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { Flexer } from '@ontruck/orc';
import { booleanize } from 'utils/converters';

import includeAnalytics from 'redux/modules/analytics/HOC';

import { StyledWrapper } from './styled';

class ShipmentFilters extends Component {
  constructor(props) {
    super(props);
    this.changeFilter = this.changeFilter.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this.changeCarrierDriverFilter = this.changeCarrierDriverFilter.bind(this);
  }

  changeFilter(name) {
    return (value) => {
      const {
        onChangeFilter = () => {
          /*noop*/
        },
        status,
        sendEvent,
        events,
      } = this.props;

      const { pathname } = window.location;

      onChangeFilter({
        ...status,
        [name]: value ? value : undefined,
      });

      try {
        sendEvent(events.FILTER, { type: name, value: value ? value : 'all', path: pathname });
      } catch (e) {
        console.error('error sending the event: ', e);
      }
    };
  }

  changeCarrierDriverFilter(change) {
    const {
      onChangeFilter = () => {
        /*noop*/
      },
      status,
      sendEvent,
      events,
    } = this.props;
    const { pathname } = window.location;

    if (!!change) {
      onChangeFilter({ ...status, [change.type]: change.value });

      try {
        sendEvent(events.FILTER, { type: 'carrierDriver', path: pathname });
      } catch (e) {
        console.error('error sending the event: ', e);
      }
      return;
    }

    onChangeFilter({ ...status, carrier: null, driver: null });
  }

  getExternalParams(item) {
    if (!item || !item.type.fieldName) {
      return {};
    }
    const { status } = this.props;
    const field = item.type.fieldName;
    const defaultValue = item.type.defaultValue;

    return {
      value: status[field] || defaultValue,
      onChange: this.changeFilter(field),
    };
  }

  handleRefresh() {
    const { onChangeFilter, status, sendEvent, events } = this.props;
    const { pathname } = window.location;
    onChangeFilter(status);

    try {
      sendEvent(events.FILTER, { type: 'refresh', path: pathname });
    } catch (e) {
      console.error('error sending the event: ', e);
    }
  }

  render() {
    const {
      wrapContent = true,
      rowMargin = 'S',
      intl,
      rows,
      beforeChildren,
      afterChildren,
    } = this.props;

    const {
      date = '',
      routes = null,
      routes_combined = null,
      status = 0,
      no_cargo_manifest = false,
      cancelled = false,
      shipper = null,
      carrier = null,
      driver = null,
      municipality = null,
      carrierDriver = null,
      handled_by = null,
      tasks_handled_by = null,
      range_date = null,
      vehicle_type = null,
      alerts = null,
      prio = null,
      countryArea = null,
      transitStatus = null,
      business = '',
      dateField = 'pickup',
      withoutVehicles = false,
      ftb = false,
    } = this.props.status;

    const defaultChildrenProps = {
      intl,
      filters: this.props.status,
      refreshFilters: this.handleRefresh,
    };

    const childrenProps = {
      DateFilter: {
        value: date,
        onChange: this.changeFilter('date'),
      },
      RangeDateFilter: {
        value: range_date,
        onChange: this.changeFilter('range_date'),
      },
      StatusFilter: {
        value: status,
        onChange: this.changeFilter('status'),
      },
      RouteFilter: {
        value: routes,
        onChange: this.changeFilter('routes'),
      },
      RoutesCombinedFilter: {
        value: routes_combined,
        onChange: this.changeFilter('routes_combined'),
      },
      CargoManifestFilter: {
        value: booleanize(no_cargo_manifest),
        onChange: this.changeFilter('no_cargo_manifest'),
      },
      CancelledFilter: {
        value: booleanize(cancelled),
        onChange: this.changeFilter('cancelled'),
      },
      ShipperFilter: {
        value: shipper,
        onChange: this.changeFilter('shipper'),
        fitWidth: 260,
      },
      CarrierDriverFilter: {
        carrier: carrier,
        driver: driver,
        carrierDriver,
        onChange: this.changeCarrierDriverFilter,
        fitWidth: 260,
      },
      MunicipalityFilter: {
        value: municipality,
        onChange: this.changeFilter('municipality'),
        fitWidth: 160,
      },
      AlertsFilter: {
        alertsValue: alerts,
        onChange: this.changeFilter('alerts'),
      },
      HandledByFilter: {
        value: handled_by,
        onChange: this.changeFilter('handled_by'),
      },
      TasksHandledByFilter: {
        value: tasks_handled_by,
        onChange: this.changeFilter('tasks_handled_by'),
      },
      VehicleTypeFilter: {
        value: vehicle_type,
        onChange: this.changeFilter('vehicle_type'),
      },
      PrioFilter: {
        value: prio,
        onChange: this.changeFilter('prio'),
      },
      CountryAreaFilter: {
        value: countryArea,
        onChange: this.changeFilter('countryArea'),
      },
      CarrierFilter: {
        value: carrier,
        onChange: this.changeFilter('carrier'),
      },
      TransitStatusFilter: {
        value: transitStatus,
        onChange: this.changeFilter('transitStatus'),
      },
      BusinessFilter: {
        value: business,
        onChange: this.changeFilter('business'),
      },
      PickupDropoffFilter: {
        value: dateField,
        onChange: this.changeFilter('dateField'),
      },
      WithoutVehicles: {
        value: withoutVehicles,
        onChange: this.changeFilter('withoutVehicles'),
      },
      FTBFilter: {
        value: ftb,
        onChange: this.changeFilter('ftb'),
      },
    };

    const filters = (
      <>
        {(rows || []).map((row, key) => (
          <Flexer.Parent key={key} alignItems="center" mBottom={rowMargin}>
            {beforeChildren}
            {(row || []).map((item, key1) => {
              const filterProps =
                childrenProps[
                  item.type.filterName ||
                    (item.type.WrappedComponent && item.type.WrappedComponent.filterName)
                ];
              return (
                item &&
                React.cloneElement(item, {
                  key: `${key}${key1}`,
                  stylingProps: {
                    last: key1 === row.length - 1,
                    fitWidth: (filterProps || {}).fitWidth,
                    grow: (item.props || {}).grow,
                  },
                  ...defaultChildrenProps,
                  ...filterProps,
                  ...this.getExternalParams(item),
                })
              );
            })}
            {afterChildren}
          </Flexer.Parent>
        ))}
      </>
    );

    if (wrapContent) {
      return (
        <StyledWrapper mTop="S" mBottom="S">
          {filters}
        </StyledWrapper>
      );
    }

    return filters;
  }
}

ShipmentFilters.propTypes = {
  intl: PropTypes.any,
  onChangeFilter: PropTypes.func,
  status: PropTypes.object,
  rows: PropTypes.array,
  beforeChildren: PropTypes.object,
  afterChildren: PropTypes.func,
  sendEvent: PropTypes.func,
  events: PropTypes.shape({}),
};

export default includeAnalytics(injectIntl(ShipmentFilters));
